import { AxiosError } from 'axios'
import { httpAuth } from 'config/apiClient'
import endpoints from 'constants/endpoints'
import { RootState } from 'constants/interfaces'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { setFullUserName, setRetailUsers, setRetailUsersObject } from './config'
import keys from 'constants/keys'
import { PopFormValues } from 'components/Pop/types'
import { getUTCOffset } from 'utils'

export const actionTypes = {
  SET_IS_FETCHING: '[ADMIN] SET_IS_FETCHING',
  SET_ACTIVE_JOBS: '[ADMIN] SET_ACTIVE_JOBS',
  SET_INACTIVE_JOBS: '[ADMIN] SET_INACTIVE_JOBS',
  UPDATE_ACTIVE_JOB: '[ADMIN] UPDATE_ACTIVE_JOB',
  UPDATE_INACTIVE_JOB: '[ADMIN] UPDATE_INACTIVE_JOB',
}

export const setIsFetching = (isFetching: boolean) => ({
  type: actionTypes.SET_IS_FETCHING,
  payload: isFetching,
})

export const setActiveJobs = (jobs: Job[]) => ({
  type: actionTypes.SET_ACTIVE_JOBS,
  payload: jobs,
})

export const setInactiveJobs = (jobs: Job[]) => ({
  type: actionTypes.SET_INACTIVE_JOBS,
  payload: jobs,
})

export const updateActiveJob = (job: Job) => ({
  type: actionTypes.UPDATE_ACTIVE_JOB,
  payload: job,
})

export const updateInactiveJob = (job: Job) => ({
  type: actionTypes.UPDATE_INACTIVE_JOB,
  payload: job,
})

export interface UserFormData {
  uid: string
  first_name: string
  last_name: string
  password: string
  email: string | null
  phone_number: string | null
  country_code: string | null
  locale: string
  permission: number | null
  default_group: string
  user_group_role: number
  desc: string
  sec_uid: string
  enable_mfa: boolean
  profile_img_url?: string
  avatar_profile_img_url?: string
}

export const deleteUser =
  (uids: string[]): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { retail_users },
      },
    } = getState()
    try {
      const updatedUsersList = retail_users?.filter((user) => !uids.includes(user.uid!))
      if (updatedUsersList) {
        await httpAuth.delete(endpoints.users, {
          data: {
            uids: uids,
            auth_context: 1,
          },
        })
        const retailUsersObject: { [key: string]: any } = {}
        dispatch(setRetailUsers(updatedUsersList))
        updatedUsersList.forEach((user: any) => {
          retailUsersObject[user.uid] = user
        })
        dispatch(setRetailUsersObject(retailUsersObject))
        return true
      }
      return false
    } catch (error) {
      console.log(`deleteUser failed due ${error}`)
      return false
    } finally {
      dispatch(setIsFetching(false))
    }
  }
export type UserObjectReturnType = {
  uid: string
  first_name: string
  last_name: string
  locale: string
  desc: string
  sec_uid: string
  user_group_role: number
}

export const createUser =
  (userObject: UserFormData): ThunkAction<Promise<UserObjectReturnType | null>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid, retail_users: usersList },
      },
    } = getState()

    try {
      dispatch(setIsFetching(true))

      const cleanUserObject = sanitizeUserObject(userObject, true)

      const payload = {
        ...cleanUserObject,
        rid,
        auth_context: 1,
      }

      await httpAuth.post(endpoints.users, payload)
      if (usersList) {
        const newUser: UserObjectReturnType = {
          last_name: userObject.last_name,
          uid: userObject.uid,
          first_name: userObject.first_name,
          locale: userObject.locale,
          desc: userObject.desc || '',
          sec_uid: userObject.sec_uid || '',
          user_group_role: userObject.user_group_role,
        }
        const updatedUsersList = [newUser, ...usersList]
        dispatch(setRetailUsers(updatedUsersList))
        const retailUsersObject: { [key: string]: any } = {}
        updatedUsersList.forEach((user: any) => {
          retailUsersObject[user.uid] = user
        })
        dispatch(setRetailUsersObject(retailUsersObject))
        return newUser
      }
      return null
    } catch (err) {
      const error: AxiosError | any = err
      console.log(`createUser failed due ${error}`)
      console.log(error)
      if (error?.response?.status === 409) {
        console.log('Server responded with error 409: Conflict')
      }
      return null
    } finally {
      dispatch(setIsFetching(false))
    }
  }

export const getUser =
  (uid: string): ThunkAction<Promise<any | null>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      const { data } = await httpAuth.get(endpoints.getUser(keys.AUTH_CONTEXT, uid))

      return data.user
    } catch (error) {
      console.log(`createUser failed due ${error}`)
    } finally {
      dispatch(setIsFetching(false))
    }
  }

export const updateUser =
  (
    data: UserFormData | null,
    isSelfAvatarChange?: boolean
  ): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid, retail_users },
      },
      auth: { uid },
    } = getState()

    if (!data || !retail_users) return false

    const existingUser = retail_users.find((user) => user.uid === data.uid)
    if (!existingUser) return false

    try {
      dispatch(setIsFetching(true))
      const updatedFields = getChangedFields(existingUser, data)

      if (Object.keys(updatedFields).length === 0 && !isSelfAvatarChange) {
        return true
      }

      const cleanUserObject = sanitizeUserObject(updatedFields as UserFormData, false)

      const payload = {
        uid: data.uid,
        ...cleanUserObject,
        ...(isSelfAvatarChange ? { rid, auth_context: 1 } : {}),
      }

      await httpAuth.put(endpoints.users, payload)

      const updatedUsers = retail_users.map((user) => (user.uid === data.uid ? { ...user, ...updatedFields } : user))

      dispatch(setRetailUsers(updatedUsers))

      const retailUsersObject: { [key: string]: any } = {}
      updatedUsers.forEach((user: any) => {
        retailUsersObject[user.uid] = user
      })
      dispatch(setRetailUsersObject(retailUsersObject))

      if (uid === data.uid) {
        dispatch(
          setFullUserName({
            firstName: updatedFields.first_name ?? existingUser.first_name,
            lastName: updatedFields.last_name ?? existingUser.last_name ?? '',
          })
        )
      }

      return true
    } catch (error) {
      console.log(`updateUser failed due to ${error}`)
      return false
    } finally {
      dispatch(setIsFetching(false))
    }
  }

interface IUpdateGroup {
  groupId: string
  added_users_ids?: string[]
  deleted_users_ids?: string[]
}
export const updateGroup =
  ({
    groupId,
    added_users_ids,
    deleted_users_ids,
  }: IUpdateGroup): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid },
      },
    } = getState()
    try {
      const payload = {
        auth_context: 1,
        rid,
        oid: groupId,
        group_data: {},
        ...(deleted_users_ids ? { deleted_users_ids } : {}),
        ...(added_users_ids ? { added_users_ids } : {}),
      }
      await httpAuth.put(endpoints.updateGroup, payload)
      return true
    } catch (error) {
      console.log(`updateGroup failed due ${error}`)
      return false
    } finally {
      dispatch(setIsFetching(false))
    }
  }

export const normalizeEmptyToNull = (value: any) => (value === '' || value === undefined ? '' : value)

export const getChangedFields = <T extends Record<string, any>>(
  original: T,
  updated: Partial<T>,
  fieldsToCompare?: (keyof T)[]
): Partial<T> => {
  const changed: Partial<T> = {}

  const keys = fieldsToCompare || (Object.keys(updated) as (keyof T)[])

  keys.forEach((key) => {
    console.log(key, original[key] || 'empty', updated[key] || 'empty')

    const originalValue = normalizeEmptyToNull(original[key])
    const newValue = normalizeEmptyToNull(updated[key])

    if (originalValue !== newValue) {
      changed[key] = newValue
    }
  })

  return changed
}

//JOBS

interface IGetJobs {
  status: number
  from_ts?: string
  to_ts?: string
}

export interface Job {
  action_reference: string
  content: {
    text: string
  }
  recurrence_interval: number
  lsi: string
  created_by: string
  recurrence_frequency: number
  status: number
  action_object_id: string
  action_service: string
  recurrence_days_of_week: number[]
  schedule_name: string
  updated_by: string
  updated_at_ts: number
  created_at_ts: number
  schedule_end_ts: string
  job_id: string
  schedule_start_ts: string
  initiator: number
  rid: string
  title: string
  action_params?: PopFormValues | null
  last_occurrence: string
  next_occurrence: string
}

export const getJobs =
  ({ status, from_ts, to_ts }: IGetJobs): ThunkAction<Promise<Job[] | null>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid },
      },
    } = getState()
    try {
      const utcOffset = getUTCOffset()
      const queryParams = {
        rid,
        status,
        from_ts,
        to_ts,
        utc_offset: utcOffset,
      }
      const { data } = await httpAuth.get<Job[]>(endpoints.jobs, { params: queryParams })

      if (status === keys.JOBS_STATUS.ACTIVE) {
        dispatch(setActiveJobs(data))
      } else if (status === keys.JOBS_STATUS.INACTIVE) {
        dispatch(setInactiveJobs(data))
      }
      return data
    } catch (error) {
      console.log(`getJobs failed due ${error}`)
      return null
    } finally {
      dispatch(setIsFetching(false))
    }
  }

export interface ICreateJob {
  is_recurring: boolean
  job_data: {
    action_object_id: string | null
    action_params: { pop_id: string; rid: string } | null
    action_queue_name: string
    action_service: string
    initiator: number
    rid: string
    schedule_start_ts: string
    schedule_end_ts: string | null
    title: string
    content: {
      text: string
    }
    recurrence_frequency: number | null
    recurrence_interval: number
    recurrence_days_of_week: number[]
  }
}

export const createJob =
  (body: ICreateJob): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid },
      },
    } = getState()
    try {
      const utcOffset = getUTCOffset()
      await httpAuth.post(endpoints.jobs, { ...body, rid, utc_offset: utcOffset })
      return true
    } catch (error) {
      console.log(`createJob failed due ${error}`)
      return false
    }
  }

export const getJobActionReference =
  (action_reference: string): ThunkAction<Promise<any | null>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      const { data } = await httpAuth.get(endpoints.jobActionReference, { params: { action_id: action_reference } })
      return data
    } catch (error) {
      console.log(`getJobActionReference failed due ${error}`)
      return null
    }
  }

// Universal function for replacing job in state (when server creates new job)
export const replaceJob =
  (newJob: Job, originalJobId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const {
      admin: { activeJobs, inactiveJobs },
    } = getState()

    // Look for original job in active jobs
    const activeJobIndex = activeJobs?.findIndex((job) => job.job_id === originalJobId)
    if (activeJobIndex !== undefined && activeJobIndex !== -1) {
      const updatedActiveJobs = [...activeJobs]
      updatedActiveJobs[activeJobIndex] = newJob
      dispatch(setActiveJobs(updatedActiveJobs))
      return
    }

    // Look for original job in inactive jobs
    const inactiveJobIndex = inactiveJobs?.findIndex((job) => job.job_id === originalJobId)
    if (inactiveJobIndex !== undefined && inactiveJobIndex !== -1) {
      const updatedInactiveJobs = [...inactiveJobs]
      updatedInactiveJobs[inactiveJobIndex] = newJob
      dispatch(setInactiveJobs(updatedInactiveJobs))
      return
    }

    console.log('Original job not found for replacement!')
  }

// Universal function for updating job in state
export const updateJobInState =
  (updatedJob: Job): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const {
      admin: { activeJobs, inactiveJobs },
    } = getState()

    // Look for job in active jobs
    const activeJobIndex = activeJobs?.findIndex((job) => job.job_id === updatedJob.job_id)
    if (activeJobIndex !== undefined && activeJobIndex !== -1) {
      dispatch(updateActiveJob(updatedJob))
    } else {
      // Look for job in inactive jobs
      const inactiveJobIndex = inactiveJobs?.findIndex((job) => job.job_id === updatedJob.job_id)
      if (inactiveJobIndex !== undefined && inactiveJobIndex !== -1) {
        dispatch(updateInactiveJob(updatedJob))
      }
    }
  }

export const updateJob =
  (job_id: string, body: ICreateJob): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid },
      },
    } = getState()

    try {
      const utcOffset = getUTCOffset()
      const { data } = await httpAuth.put(endpoints.jobs, { ...body, rid, utc_offset: utcOffset, job_id })

      // Use replaceJob since server creates new job instead of updating existing one
      const newJob = data as Job
      dispatch(replaceJob(newJob, job_id))

      return true
    } catch (error) {
      console.log(`updateJob failed due ${error}`)
      return false
    }
  }

export const updateJobStatus =
  (job_id: string, status: number): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: {
        config: { rid },
      },
    } = getState()
    try {
      await httpAuth.put(endpoints.updateJobStatus, { job_id, status, rid })
      return true
    } catch (error) {
      console.log(`updateJobStatus failed due ${error}`)
      return false
    }
  }

export const sanitizeUserObject = (userObject: UserFormData, isCreate: boolean = false) => {
  const cleanUserObject: Partial<UserFormData> = { ...userObject }

  if (!cleanUserObject.phone_number || cleanUserObject.phone_number.trim() === '') {
    if (isCreate) {
      delete cleanUserObject.phone_number
    } else {
      cleanUserObject.phone_number = null
    }
  }
  if (!cleanUserObject.country_code || cleanUserObject.country_code.trim() === '') {
    if (isCreate) {
      delete cleanUserObject.country_code
    } else {
      cleanUserObject.country_code = null
    }
  }
  if (!cleanUserObject.email || cleanUserObject.email.trim() === '') {
    if (isCreate) {
      delete cleanUserObject.email
    } else {
      cleanUserObject.email = null
    }
  }

  return cleanUserObject
}
